import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';

import { resetCart, setSearch } from '@/AppRedux/actions/booking';
import { SearchState } from '@/AppRedux/actions/booking/bookingType';
import { HotelSettings } from '@/AppRedux/actions/hotelProfile/hotelType';
import { isCheckInDateBlocked } from '@/utils/bookingCutoff';

interface UseBookingSearchActionProps {
  propcode?: string;
  onSearchOverride?: (values: SearchState) => void;
  hotelSettings?: HotelSettings | null;
}

export const useBookingSearchAction = ({
  propcode,
  onSearchOverride,
  hotelSettings,
}: UseBookingSearchActionProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [blockedSearch, setBlockedSearch] = useState<SearchState | null>(null);

  const updateLiveSearch = (newValues: Partial<SearchState>) => {
    dispatch(setSearch(newValues));
  };

  const handleDateChange = (vals: [Dayjs | null, Dayjs | null] | null) => {
    if (vals && vals[0] && vals[1]) {
      dispatch(
        setSearch({
          checkIn: vals[0].format('YYYY-MM-DD'),
          checkOut: vals[1].format('YYYY-MM-DD'),
        })
      );
    } else {
      dispatch(setSearch({ checkIn: '', checkOut: '' }));
    }
  };

  const handleAdultsChange = (val: number) => {
    dispatch(setSearch({ adults: val || 2, guestTouched: true }));
  };

  const handleChildrenChange = (val: number) => {
    dispatch(setSearch({ children: val || 0, guestTouched: true }));
  };

  const handleInfantsChange = (val: number) => {
    dispatch(setSearch({ infants: val || 0, guestTouched: true }));
  };

  const triggerSearch = (currentSearchState: SearchState) => {
    const checkInDate = currentSearchState.checkIn ? dayjs(currentSearchState.checkIn) : null;
    if (isCheckInDateBlocked(checkInDate, hotelSettings)) {
      setBlockedSearch(currentSearchState);
      return;
    }

    dispatch(resetCart());
    if (onSearchOverride) {
      onSearchOverride(currentSearchState);
      return;
    }

    if (!currentSearchState.checkIn || !currentSearchState.checkOut || !propcode) {
      return;
    }

    const params = new URLSearchParams({
      start_date: currentSearchState.checkIn,
      end_date: currentSearchState.checkOut,
      adults: String(currentSearchState.adults ?? 2),
      children: String(currentSearchState.children ?? 0),
      infants: String(currentSearchState.infants ?? 0),
    });

    navigate(`/booking/${propcode}/availability?${params.toString()}`);
  };

  const closeCutoffModal = () => setBlockedSearch(null);

  const confirmSearchTomorrow = () => {
    if (!blockedSearch) return;

    const nights =
      blockedSearch.checkIn && blockedSearch.checkOut
        ? Math.max(1, dayjs(blockedSearch.checkOut).diff(dayjs(blockedSearch.checkIn), 'day'))
        : 1;
    const newCheckIn = dayjs().add(1, 'day').startOf('day');
    const nextState: SearchState = {
      ...blockedSearch,
      checkIn: newCheckIn.format('YYYY-MM-DD'),
      checkOut: newCheckIn.add(nights, 'day').format('YYYY-MM-DD'),
    };

    dispatch(setSearch({ checkIn: nextState.checkIn, checkOut: nextState.checkOut }));
    setBlockedSearch(null);
    triggerSearch(nextState);
  };

  return {
    updateLiveSearch,
    handleDateChange,
    handleAdultsChange,
    handleChildrenChange,
    handleInfantsChange,
    triggerSearch,
    isCutoffModalOpen: !!blockedSearch,
    closeCutoffModal,
    confirmSearchTomorrow,
  };
};
